/**
 * O botão "Abrir ..." de cada dúvida: o lugar onde aquilo se resolve, como LINK.
 *
 * Ele substituiu a linha "Onde fica", que era um caminho escrito ("Configurações ›
 * Prazo e programação › Modo de programação") e deixava o último passo para a pessoa.
 * O destino padrão é a primeira tela da dúvida; a dúvida que se resolve num bloco certo
 * de outra tela declara o dela aqui, e é só isso que esta lista guarda.
 */
import type { TFunction } from 'i18next';

import { BLOCO_PARAM, SECAO_PARAM } from '@/app/settings/helpers/settingsBlocks';

import { MAPA_CARTOES_ABERTA, MAPA_CARTOES_PARAM } from '../../mapa/helpers/parametrosDoMapa';
import { IAjudaDuvida, IAjudaTela } from './ajudaConteudo';
import { telasDaDuvida } from './ajudaTarefas';

export interface IDestinoDaDuvida {
  href: string;
  /** O nome do lugar, como ele aparece na tela de destino. */
  rotulo: string;
}

interface IDestinoProprio {
  href: string;
  labelKey: string;
}

/** O bloco de Configurações que responde a dúvida, pelo nome que o próprio bloco tem. */
function bloco(id: string): IDestinoProprio {
  return { href: `/settings?${BLOCO_PARAM}=${id}`, labelKey: `settings.indice.bloco.${id}` };
}

/**
 * As dúvidas cuja resposta mora num lugar MAIS exato do que a tela: um bloco das
 * Configurações, o cartão da Implantação, a janela de cartões do Mapa. O resto cai na
 * tela da dúvida, que é onde ela se resolve.
 */
const DESTINO_PROPRIO: Readonly<Record<string, IDestinoProprio>> = {
  feriados: bloco('jornada'),
  modoProgramacao: bloco('programacao'),
  janelaCongelada: bloco('programacao'),
  objetivoDaOtimizacao: bloco('programacao'),
  produtividadeSobreRitmo: bloco('prazo'),
  desfazer: bloco('historico'),
  desfazerProposta: bloco('historico'),
  participacao: bloco('celulas'),
  checklist: bloco('liberacao'),
  decidirFila: bloco('fila'),
  faltaValidar: { href: `/settings?${SECAO_PARAM}=implantacao`, labelKey: 'settings.indice.secao.implantacao' },
  implantacao: { href: `/settings?${SECAO_PARAM}=implantacao`, labelKey: 'settings.indice.secao.implantacao' },
  ondeFicaConfiguracao: { href: '/settings', labelKey: 'settings.title' },
  ajudaDaTela: { href: '/ajuda', labelKey: 'ajuda.title' },
  cartoesDoMapa: {
    href: `/mapa?${MAPA_CARTOES_PARAM}=${MAPA_CARTOES_ABERTA}`,
    labelKey: 'configuracao.sections.mapaCartao',
  },
};

/** Para onde o botão da dúvida leva, ou `null` quando ela não se resolve em tela nenhuma. */
export function destinoDaDuvida(
  duvida: IAjudaDuvida,
  telas: ReadonlyArray<IAjudaTela>,
  t: TFunction,
): IDestinoDaDuvida | null {
  const proprio = DESTINO_PROPRIO[duvida.id];
  if (proprio !== undefined) {
    return { href: proprio.href, rotulo: t(proprio.labelKey) };
  }
  const primeira = telasDaDuvida(duvida.id)[0];
  const tela = telas.find((candidata) => candidata.id === primeira);
  return tela === undefined ? null : { href: tela.rota, rotulo: tela.nome };
}
